/**
 * AI Car Search Module
 * Handles form submission, loading state, and panel rendering for the AI car search UI.
 */

import DOMPurify from 'dompurify';
import Mustache from 'mustache';

const API_ENDPOINT = '/stage-ajax/ai-car-search-get';

/** Animation timing constants — keep in sync with CSS transition durations. */
const TIMINGS = {
  /** Duration of the panel content exit scale animation (ms). */
  panelContentExit: 250,
  /** Duration of the panel box height transition (ms). */
  panelHeightTransition: 400,
  /** Extra buffer after height shrink before revealing incoming content (ms). */
  panelShrinkSettle: 370,
  /** Extra buffer after height expand before clearing inline height styles (ms). */
  panelExpandClear: 420,
  /** Time to wait after scale-in before starting typewriter / calling onComplete (ms). */
  contentScaleSettle: 380,
  /** Delay before the results grid row starts collapsing (ms). */
  resultsContentFade: 300,
  /** Delay before clearing results innerHTML after grid collapses (ms). */
  resultsGridCollapse: 400,
  /** Delay before showing results content after grid expands (ms). */
  resultsContentReveal: 350,
  /** Delay before collapsing model area / expanding vehicle area after content fades (ms). */
  vehicleTransitionFade: 150,
  /** Delay before showing vehicle results content after the vehicle area starts expanding (ms). */
  vehicleResultsReveal: 200,
  /** Stagger delay between consecutive follow-up items (ms). */
  followupItemStagger: 150,
  /** Duration of a single follow-up item transition (ms). */
  followupItemTransition: 400,
  /** Duration of the loading text exit transition before the next span appears (ms). */
  loadingTextTransitionOut: 300,
  /** When the first loading text change occurs (ms). */
  loadingTextFirstChange: 4000,
  /** When the last loading text change occurs (ms). */
  loadingTextLastChange: 8000,
};

/**
 * @param {string} label - Accessible label for the button
 * @returns {string} HTML string for the loading submit button
 */
const LOADING_BUTTON_HTML = (label) => `
<button type="submit" class="hedin-btn hedin-btn--default ai-car-search__submit--loading" aria-label="${label}" aria-busy="true" disabled>
  <svg class="ai-car-search__spinner ai-car-search__spinner--button" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="11"   cy="3"    r="1.1" fill="#000"/>
    <circle cx="15.7" cy="4.5"  r="1.1" fill="#000"/>
    <circle cx="18.6" cy="8.5"  r="1.1" fill="#000"/>
    <circle cx="18.6" cy="13.5" r="1.1" fill="#000"/>
    <circle cx="15.7" cy="17.5" r="1.1" fill="#000"/>
    <circle cx="11"   cy="19"   r="1.1" fill="#000"/>
    <circle cx="6.3"  cy="17.5" r="1.1" fill="#000"/>
    <circle cx="3.4"  cy="13.5" r="1.1" fill="#000"/>
    <circle cx="3.4"  cy="8.5"  r="1.1" fill="#000"/>
    <circle cx="6.3"  cy="4.5"  r="1.1" fill="#000"/>
  </svg>
</button>`.trim();

/**
 * @param {string} label - Accessible label for the button
 * @returns {string} HTML string for the idle submit button
 */
const IDLE_BUTTON_HTML = (label) =>
  `<button type="submit" class="hedin-btn hedin-btn--default f-icon f-icon--arrow-right f-icon--no-margin" aria-label="${label}"></button>`;

/**
 * @param {string} label - Visible label text for the tag
 * @param {string} filter - Filter value stored in data-filter
 * @returns {string} HTML string for a selected tag chip button
 */
const SELECTED_TAG_HTML = (label, filter) =>
  `<button type="button" class="ai-car-search__selected-tag" data-filter="${filter}">${label}<span class="ai-car-search__selected-tag-icon" aria-hidden="true"></span></button>`;


class AiCarSearch {
  /** @type {Element|null} */
  #section;
  /** @type {HTMLFormElement} */
  #form;
  /** @type {Object} */
  #dom = {};
  /** @type {ReturnType<typeof setTimeout>|null} */
  #resultsHideTimer = null;
  /** @type {Object[]} */
  #vehicleData = [];
  /** @type {Object|null} */
  #vehicleTemplates = null;
  /** @type {string} */
  #currentSort = '';
  /** @type {string} */
  #currentBrandUuid = '';
  /** @type {string} */
  #currentModelUuid = '';
  /** @type {boolean} */
  #vehicleStateInHistory = false;
  /** @type {boolean} */
  #isLoading = false;
  /** @type {string} */
  #loadingTemplate;
  /** @type {string} */
  #responseTemplate;
  /** @type {string} */
  #carCardTemplate;
  /** @type {string} */
  #carListTemplate;
  /** @type {string} */
  #errorTemplate;
  /** @type {string} */
  #currentQuery = '';
  /** @type {string} */
  #currentDescription = '';
  /** @type {boolean} */
  #lastSearchWasSuggestion = false;
  /** @type {number} */
  #followupCount = 0;
  /** @type {{ label: string, filter: string }[]} */
  #selectedTags = [];
  /** @type {AbortController|null} */
  #abortController = null;
  /** @type {ReturnType<typeof setTimeout>[]} */
  #loadingTextTimers = [];
  /** @type {HTMLElement} */
  #announcer;

  /**
   * @param {HTMLFormElement} form - The search form element
   */
  constructor(form) {
    this.#form = form;
    this.#section = form.closest('.ai-car-search');
    this.#queryElements(form);

    if (!this.#dom.input || !this.#dom.field) {
      console.error('[AiCarSearch] Required elements .ai-car-search__input or .ai-car-search__field not found.');
      return;
    }

    // Visually hidden live region for screen reader announcements.
    this.#announcer = document.createElement('p');
    this.#announcer.setAttribute('aria-live', 'polite');
    this.#announcer.setAttribute('aria-atomic', 'true');
    this.#announcer.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
    (this.#section ?? document.body).appendChild(this.#announcer);

    this.#loadingTemplate = document.getElementById('AICarSearchLoadingPanel')?.innerHTML ?? '';
    this.#responseTemplate = document.getElementById('AICarSearchResponsePanel')?.innerHTML ?? '';
    this.#carCardTemplate = document.getElementById('AICarSearchCarCard')?.innerHTML ?? '';
    this.#carListTemplate = document.getElementById('AICarSearchResults')?.innerHTML ?? '';
    this.#errorTemplate = document.getElementById('AICarSearchErrorPanel')?.innerHTML ?? '';

    const templateNames = [
      ['AICarSearchLoadingPanel', this.#loadingTemplate],
      ['AICarSearchResponsePanel', this.#responseTemplate],
      ['AICarSearchCarCard', this.#carCardTemplate],
      ['AICarSearchResults', this.#carListTemplate],
      ['AICarSearchErrorPanel', this.#errorTemplate],
    ];
    templateNames.forEach(([id, tpl]) => {
      if (!tpl.trim()) {
        console.warn(`[AiCarSearch] Template #${id} is missing or empty.`);
      }
    });

    this.#bindEvents();
    this.#restoreState();
  }

  /**
   * Queries and assigns all DOM element references for the section and form.
   * @param {HTMLFormElement} form - The search form element
   */
  #queryElements(form) {
    this.#dom = {
      panel: this.#section?.querySelector('.js-ai-car-search-panel'),
      results: this.#section?.querySelector('.js-ai-car-search-results'),
      resultsArea: this.#section?.querySelector('.js-ai-car-search-results-area'),
      resultsContent: this.#section?.querySelector('.js-ai-car-search-results-content'),
      vehicleResults: this.#section?.querySelector('.js-ai-car-search-vehicle-results'),
      vehicleResultsGrid: this.#section?.querySelector('.cars.js-ai-car-search-vehicle-results'),
      vehicleResultCount: this.#section?.querySelector('.js-ai-car-search-vehicle-result-count'),
      vehicleResultsArea: this.#section?.querySelector('.js-ai-car-search-vehicle-results-area'),
      vehicleResultsContent: this.#section?.querySelector('.js-ai-car-search-vehicle-results-content'),
      input: form.querySelector('.ai-car-search__input'),
      field: form.querySelector('.ai-car-search__field'),
      selectedTagsList: this.#section?.querySelector('.js-selected-tags'),
      stageTokenInput: form.querySelector('input[name="stage_token"]'),
      noDescriptionInput: form.querySelector('input[name="noDescription"]'),
      noFollowUpInput: form.querySelector('input[name="noFollowUp"]'),
      noModelDescriptionInput: form.querySelector('input[name="noModelDescription"]'),
      sorting: this.#section?.querySelector('.js-ai-car-search-sorting'),
    };
  }

  /**
   * Attaches submit, keydown, and delegated click event listeners.
   */
  #bindEvents() {
    this.#form.addEventListener('submit', this.#handleSubmit.bind(this));

    this.#dom.input.addEventListener('input', () => this.#resizeInput());
    window.addEventListener('resize', () => this.#resizeInput());
    this.#resizeInput();

    // Submit on Enter (textarea doesn't submit on Enter by default)
    this.#dom.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (this.#form.requestSubmit) {
          this.#form.requestSubmit();
        } else {
          this.#form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }
    });

    if (!this.#section) return;

    this.#section.addEventListener('click', (e) => {
      const resetBtn = e.target.closest('.js-ai-car-search-reset');
      if (resetBtn) {
        this.#handleReset();
        return;
      }

      const selectedTag = e.target.closest('.ai-car-search__selected-tag');
      if (selectedTag) {
        this.#handleSelectedTagRemove(selectedTag.dataset.filter);
        return;
      }

      const chip = e.target.closest('.ai-car-search__chips .hedin-btn');
      if (chip) {
        this.#handleChipClick(chip);
        return;
      }

      const suggestion = e.target.closest('.js-search-suggestion');
      if (suggestion) {
        this.#handleSuggestionClick(suggestion);
        return;
      }

      const vehicleBack = e.target.closest('.js-ai-car-search-results-back');
      if (vehicleBack) {
        if (this.#vehicleStateInHistory) {
          history.back();
        } else {
          this.#hideVehicleResults();
        }
        return;
      }

      const modelCard = e.target.closest('[data-brand-uuid][data-model-uuid]');
      if (modelCard && this.#dom.results?.contains(modelCard)) {
        e.preventDefault();
        const index = Array.from(this.#dom.results.querySelectorAll('[data-brand-uuid]')).indexOf(modelCard);

        window.dataLayer.push({
          'event': 'car_model_selected',
          'text_input_value': this.#selectedTags.length > 0 ? this.#selectedTags.at(-1).filter : this.#currentQuery,
          'search_position': index + 1, // 1-based index
          'car_brand': modelCard.dataset.brandName || '',
          'car_model': modelCard.dataset.modelName || '',
        });
        this.#showVehicleResults(modelCard.dataset.brandUuid, modelCard.dataset.modelUuid);
      }

      const vehicleCard = e.target.closest('a.card');
      if (vehicleCard && this.#dom.vehicleResultsGrid?.contains(vehicleCard)) {
        this.#saveState();
        const vehicle = this.#vehicleData.find((v) => v.license === vehicleCard.dataset.license);
        if (vehicle) {
          try {
            sessionStorage.setItem('aiCarSearchVehicleContext', JSON.stringify({
              description: this.#currentDescription,
              label: vehicle.labels?.[0]?.name ?? '',
            }));
          } catch {
            // sessionStorage unavailable or quota exceeded
          }
        }
      }
    });

    if (this.#dom.sorting) {
      this.#dom.sorting.addEventListener('tertiaryChange', (e) => {
        const value = e.detail?.value ?? '';
        this.#currentSort = value;
        if (this.#currentBrandUuid && this.#currentModelUuid) {
          this.#rerenderVehicleResults();
        }
      });
    }

    window.addEventListener('popstate', () => {
      if (this.#dom.vehicleResultsArea?.classList.contains('is-visible')) {
        this.#hideVehicleResults();
      }
    });
  }

  /**
   * @param {SubmitEvent} event - The form submit event
   */
  #handleSubmit(event) {
    event.preventDefault();
    if (this.#isLoading) return;
    const query = this.#dom.input.value.trim();
    if (!query) return;

    this.#currentQuery = query;
    this.#fetchResults(query, this.#selectedTags);
    this.#lastSearchWasSuggestion = false;
  }

  /**
   * Fetches AI search results and renders the loading and response panels.
   * @param {string} query - The user's search query
   * @param {{ label: string, filter: string }[]} tags - Active refinement tags appended to the request
   */
  async #fetchResults(query, tags) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#isLoading = true;
    this.#section?.classList.add('is-searched');
    this.#setLoadingState(true);
    this.#announce('Haetaan tuloksia…');
    this.#hideResults();
    this.#renderPanel(Mustache.render(this.#loadingTemplate), null, () => this.#cycleLoadingText());

    const url = new URL(API_ENDPOINT, window.location.origin);
    url.searchParams.set('query', query);
    url.searchParams.set('is_suggestion', this.#lastSearchWasSuggestion);
    url.searchParams.set('followup_number', this.#followupCount);

    tags.forEach((tag) => url.searchParams.append('tags[]', tag.filter));

    if (this.#dom.stageTokenInput?.value) {
      url.searchParams.set('stage_token', this.#dom.stageTokenInput.value);
    }

    if (this.#dom.noDescriptionInput) {
      url.searchParams.set('noDescription', this.#dom.noDescriptionInput.value);
    }

    if (this.#dom.noFollowUpInput) {
      url.searchParams.set('noFollowUp', this.#dom.noFollowUpInput.value);
    }

    if (this.#dom.noModelDescriptionInput) {
      url.searchParams.set('noModelDescription', this.#dom.noModelDescriptionInput.value);
    }

    try {
      const res = await fetch(url.toString(), { signal });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json?.error) {
        throw new Error('API returned error');
      }
      const data = json?.data;
      if (!data || typeof data !== 'object') {
        throw new Error('API response missing data object');
      }

      if (json.analytics) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(json.analytics);

        // Increment count for the NEXT search if it was a search event
        if (json.analytics.event === 'ai_car_search' || json.analytics.event === 'ai_car_search_followup') {
          this.#followupCount++;
        }
      }

      const { description, followUp, modelCount, modelData, modelDescriptions, stage_token, vehicleData, templates } = data;
      this.#vehicleData = Array.isArray(vehicleData) ? vehicleData : [];
      this.#vehicleTemplates = (templates && typeof templates === 'object') ? templates : null;
      this.#currentDescription = description ?? '';

      if (stage_token && this.#dom.stageTokenInput) {
        this.#dom.stageTokenInput.value = stage_token;
      }

      let questions = [];
      if (Array.isArray(followUp)) {
        questions = followUp.map((item) => {
          const chips = (item.tags ?? []).map((tag) => ({
            label: tag.label ?? '',
            value: tag.filter ?? '',
          }));
          return { question: DOMPurify.sanitize(item.question ?? ''), chips };
        });
      }

      const responseData = {
        response_query: DOMPurify.sanitize(query),
        response_text: DOMPurify.sanitize(description ?? ''),
        followup_questions: questions.length > 0,
        questions,
      };

      this.#renderCarResults(
        Array.isArray(modelData) ? modelData : [],
        Array.isArray(modelDescriptions) ? modelDescriptions : [],
        modelCount || 0,
      );
      this.#renderPanel(Mustache.render(this.#responseTemplate, responseData), () => {
        this.#isLoading = false;
        this.#clearLoadingTextTimers();
        this.#setLoadingState(false);
        this.#scrollPanelIntoView();
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        this.#isLoading = false;
        this.#clearLoadingTextTimers();
        this.#setLoadingState(false);
        return;
      }
      console.error('[AiCarSearch] Fetch failed:', err);
      this.#isLoading = false;
      this.#clearLoadingTextTimers();
      this.#setLoadingState(false);
      this.#announce('Haussa tapahtui virhe. Yritä uudelleen.');
      this.#renderPanel(DOMPurify.sanitize(this.#errorTemplate));
    }
  }

  /**
   * Resets the form and clears all results and panel state.
   */
  #handleReset() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#isLoading = false;
    this.#clearLoadingTextTimers();
    this.#dom.input.value = '';
    this.#dom.input.style.height = '';
    this.#currentQuery = '';
    this.#currentDescription = '';
    this.#selectedTags = [];
    this.#followupCount = 0;
    sessionStorage.removeItem('aiCarSearchVehicleContext');
    this.#renderSelectedTags();
    this.#section?.classList.remove('is-searched');
    this.#setLoadingState(false);
    this.#clearPanel();
    this.#clearVehicleResults();
    this.#renderCarResults([]);
    this.#dom.input.focus();
    this.#refreshStageToken();
  }

  /**
   * Fetches a fresh stage_token and writes it to the hidden input.
   */
  async #refreshStageToken() {
    if (!this.#dom.stageTokenInput) return;
    try {
      const res = await fetch('/stage-ajax/stage-token');
      if (!res.ok) return;
      const json = await res.json();
      if (json?.stage_token) this.#dom.stageTokenInput.value = json.stage_token;
    } catch {
      // Non-critical — next search will use the existing token
    }
  }

  /**
   * Renders car model cards into the results container.
   * @param {Object[]} models - Array of model data objects from the API
   * @param {Object[]} modelDescriptions - Array of AI-generated descriptions keyed by make/model
   * @param {number} modelCount - Total number of models returned by the API
   */
  #renderCarResults(models, modelDescriptions = [], modelCount = 0) {
    if (!this.#dom.results) {
      return;
    }

    if (!models.length) {
      this.#hideResults();
      return;
    }

    clearTimeout(this.#dom.resultsHideTimer);
    this.#dom.resultsHideTimer = null;

    const data = {
      modelCount,
      models: models.map((model) => {
        const match = modelDescriptions.find(
          (desc) =>
            desc.make?.toLowerCase() === model.brand_name?.toLowerCase() &&
            desc.model?.toLowerCase() === model.model_name?.toLowerCase(),
        );
        const count = model.vehicle_count ?? 0;
        const vehicleCountLabel = count === 1 ? `${count} vaihtoehto` : `${count} vaihtoehtoa`;
        return match
          ? { ...model, description: DOMPurify.sanitize(match.description ?? ''), vehicle_count_label: vehicleCountLabel }
          : { ...model, vehicle_count_label: vehicleCountLabel };
      }),
    };

    this.#dom.results.innerHTML = DOMPurify.sanitize(Mustache.render(this.#carListTemplate, data, { card: this.#carCardTemplate }));

    const cardEls = this.#dom.results.querySelectorAll('.card');
    models.forEach((model, i) => {
      if (cardEls[i]) {
        cardEls[i].dataset.brandUuid = model.brand_uuid ?? '';
        cardEls[i].dataset.modelUuid = model.model_uuid ?? '';
      }
    });

    if (this.#dom.resultsArea) {
      this.#dom.resultsArea.classList.add('is-visible');
      setTimeout(() => {
        if (this.#dom.resultsContent) this.#dom.resultsContent.classList.add('is-visible');
      }, TIMINGS.resultsContentReveal);
    }
  }

  /**
   * Filters vehicleData by brand/model uuid, renders cards using the API-supplied
   * basicCard Mustache template, dims the model listing, then reveals vehicle cards.
   * @param {string} brandUuid - Brand UUID to filter vehicles by
   * @param {string} modelUuid - Model UUID to filter vehicles by
   */
  #showVehicleResults(brandUuid, modelUuid) {
    if (!this.#dom.vehicleResultsGrid || !this.#dom.vehicleResultsArea || !this.#dom.vehicleResultsContent) return;

    const filtered = this.#vehicleData.filter(
      (v) => v.brand_uuid === brandUuid && v.model_uuid === modelUuid,
    );
    if (!filtered.length) return;

    this.#currentBrandUuid = brandUuid;
    this.#currentModelUuid = modelUuid;

    if (!this.#vehicleStateInHistory) {
      history.pushState({ aiCarSearchVehicle: true }, '');
      this.#vehicleStateInHistory = true;
    }

    const tpl = this.#vehicleTemplates?.basicCard;
    if (!tpl?.template) return;

    const { template: cardTemplate, partials = {}, cardCustomClasses = '' } = tpl;

    const rawHtml = filtered
      .map((vehicle) => window.Dali.render(cardTemplate, { ...vehicle, cardCustomClasses }, partials))
      .join('');

    // Pre-render into DOM while still invisible so layout is ready for the grid expand
    this.#dom.vehicleResultsContent.classList.remove('is-visible');
    this.#dom.vehicleResultsGrid.innerHTML = DOMPurify.sanitize(rawHtml);
    this.#rerenderVehicleResults();
    if (this.#dom.vehicleResultCount) this.#dom.vehicleResultCount.textContent = filtered.length;

    // Step 1: fade model content out
    if (this.#dom.resultsContent) this.#dom.resultsContent.classList.add('is-browsing-vehicles');

    // Step 2: after model content has faded, swap areas instantly (no grid slide)
    setTimeout(() => {
      if (this.#dom.resultsArea) {
        this.#dom.resultsArea.style.transition = 'none';
        this.#dom.resultsArea.classList.remove('is-visible');
      }
      this.#dom.vehicleResultsArea.style.transition = 'none';
      this.#dom.vehicleResultsArea.classList.add('is-visible');

      requestAnimationFrame(() => {
        if (this.#dom.resultsArea) this.#dom.resultsArea.style.transition = '';
        this.#dom.vehicleResultsArea.style.transition = '';
        this.#scrollIntoViewIfAbove(this.#dom.vehicleResultsArea);
      });

      // Step 3: after vehicle grid has expanded, fade vehicle content in
      setTimeout(() => {
        this.#dom.vehicleResultsContent.classList.add('is-visible');
      }, TIMINGS.vehicleResultsReveal);
    }, TIMINGS.vehicleTransitionFade);
  }

  /**
   * Collapses the vehicle results area and restores the model listing.
   */
  #hideVehicleResults() {
    if (!this.#dom.vehicleResultsArea || !this.#dom.vehicleResultsContent) return;

    this.#vehicleStateInHistory = false;

    // Step 1: fade vehicle content out
    this.#dom.vehicleResultsContent.classList.remove('is-visible');

    // Step 2: after vehicle content fades, swap areas instantly (no grid slide)
    setTimeout(() => {
      this.#dom.vehicleResultsArea.style.transition = 'none';
      this.#dom.vehicleResultsArea.classList.remove('is-visible');
      if (this.#dom.resultsArea) {
        this.#dom.resultsArea.style.transition = 'none';
        this.#dom.resultsArea.classList.add('is-visible');
      }

      requestAnimationFrame(() => {
        this.#dom.vehicleResultsArea.style.transition = '';
        if (this.#dom.resultsArea) this.#dom.resultsArea.style.transition = '';
        this.#scrollIntoViewIfAbove(this.#dom.resultsArea);
      });

      // Step 3: after model grid has expanded, fade model content back in
      setTimeout(() => {
        if (this.#dom.resultsContent) this.#dom.resultsContent.classList.remove('is-browsing-vehicles');
      }, TIMINGS.vehicleResultsReveal);

      // Clear vehicle DOM once its grid has fully collapsed
      setTimeout(() => {
        if (this.#dom.vehicleResultsGrid) this.#dom.vehicleResultsGrid.innerHTML = '';
        if (this.#dom.vehicleResultCount) this.#dom.vehicleResultCount.textContent = '0';
      }, TIMINGS.resultsGridCollapse);
    }, TIMINGS.vehicleTransitionFade);
  }

  /**
   * Immediately removes vehicle results from the DOM without animation.
   * Called on reset and at the start of a new search.
   */
  #clearVehicleResults() {
    if (this.#dom.vehicleResultsContent) this.#dom.vehicleResultsContent.classList.remove('is-visible');
    if (this.#dom.vehicleResultsArea) this.#dom.vehicleResultsArea.classList.remove('is-visible');
    if (this.#dom.vehicleResultsGrid) this.#dom.vehicleResultsGrid.innerHTML = '';
    if (this.#dom.vehicleResultCount) this.#dom.vehicleResultCount.textContent = '0';
    if (this.#dom.resultsContent) this.#dom.resultsContent.classList.remove('is-browsing-vehicles');
    this.#vehicleData = [];
    this.#vehicleTemplates = null;
    this.#currentBrandUuid = '';
    this.#currentModelUuid = '';
    this.#vehicleStateInHistory = false;
  }

  /**
   * Serialises current search state to sessionStorage so it can be recovered
   * when the user navigates back from a vehicle page.
   */
  #saveState() {
    try {
      sessionStorage.setItem('aiCarSearchState', JSON.stringify({
        query: this.#currentQuery,
        selectedTags: this.#selectedTags,
        description: this.#currentDescription,
        panelHTML: this.#dom.panel?.innerHTML ?? '',
        resultsHTML: this.#dom.results?.innerHTML ?? '',
        vehicleData: this.#vehicleData,
        vehicleTemplates: this.#vehicleTemplates,
        vehicleResultsHTML: this.#dom.vehicleResultsGrid?.innerHTML ?? '',
        vehicleResultsVisible: this.#dom.vehicleResultsArea?.classList.contains('is-visible') ?? false,
        vehicleResultCount: this.#dom.vehicleResultCount?.textContent ?? '0',
        currentBrandUuid: this.#currentBrandUuid,
        currentModelUuid: this.#currentModelUuid,
      }));
    } catch {
      // sessionStorage unavailable or quota exceeded — skip silently
    }
  }

  /**
   * Restores search state from sessionStorage when arriving via the browser
   * back/forward cache or when the URL contains "restore=1". The restore param
   * is stripped from the URL after use. All other navigations clear saved state.
   */
  #restoreState() {
    const raw = sessionStorage.getItem('aiCarSearchState');
    if (!raw) return;

    const navType = performance?.getEntriesByType?.('navigation')[0]?.type;
    const url = new URL(window.location.href);
    const hasRestoreParam = url.searchParams.get('restore') === '1';

    sessionStorage.removeItem('aiCarSearchState');
    if (navType !== 'back_forward' && !hasRestoreParam) return;

    if (hasRestoreParam) {
      url.searchParams.delete('restore');
      history.replaceState(null, '', url);
    }

    let state;
    try {
      state = JSON.parse(raw);
    } catch {
      return;
    }

    if (!state?.query) return;

    this.#currentQuery = state.query;
    this.#currentDescription = state.description ?? '';
    this.#selectedTags = Array.isArray(state.selectedTags) ? state.selectedTags : [];
    this.#vehicleData = Array.isArray(state.vehicleData) ? state.vehicleData : [];
    this.#vehicleTemplates = state.vehicleTemplates ?? null;

    this.#dom.input.value = state.query;
    this.#resizeInput();
    this.#renderSelectedTags();
    this.#section?.classList.add('is-searched');

    if (state.panelHTML && this.#dom.panel) {
      this.#dom.panel.innerHTML = state.panelHTML;
      this.#dom.panel.classList.add('is-visible');
    }

    if (state.resultsHTML && this.#dom.results) {
      this.#dom.results.innerHTML = state.resultsHTML;
    }

    if (state.vehicleResultsVisible && state.vehicleResultsHTML && this.#dom.vehicleResultsGrid) {
      this.#dom.vehicleResultsGrid.innerHTML = state.vehicleResultsHTML;
      if (this.#dom.vehicleResultCount) this.#dom.vehicleResultCount.textContent = state.vehicleResultCount ?? '0';
      this.#currentBrandUuid = state.currentBrandUuid ?? '';
      this.#currentModelUuid = state.currentModelUuid ?? '';
      if (this.#dom.vehicleResultsArea) this.#dom.vehicleResultsArea.classList.add('is-visible');
      if (this.#dom.vehicleResultsContent) this.#dom.vehicleResultsContent.classList.add('is-visible');
      if (this.#dom.resultsContent) this.#dom.resultsContent.classList.add('is-visible', 'is-browsing-vehicles');
      requestAnimationFrame(() => {
        this.#dom.vehicleResultsArea?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      if (this.#dom.resultsArea) this.#dom.resultsArea.classList.add('is-visible');
      if (this.#dom.resultsContent) this.#dom.resultsContent.classList.add('is-visible');
    }

    this.#repopulateVehicleContext();
  }

  /**
   * Reads aiCarSearchVehicleContext from sessionStorage and repopulates
   * .js-car-page-ai-description and .js-car-page-ai-label within the section.
   * Called after restoring innerHTML so dynamically-set content is not lost.
   */
  #repopulateVehicleContext() {
    const raw = sessionStorage.getItem('aiCarSearchVehicleContext');
    if (!raw) return;
    try {
      const { description, label } = JSON.parse(raw);
      const descEl = this.#section?.querySelector('.js-car-page-ai-description');
      const labelEl = this.#section?.querySelector('.js-car-page-ai-label');
      if (descEl && description) descEl.textContent = description;
      if (labelEl && label) labelEl.textContent = label;
    } catch {
      // ignore malformed data
    }
  }

  /**
   * Returns a sorted copy of the given vehicles array based on #currentSort.
   * @param {Object[]} vehicles - Vehicle data objects to sort
   * @returns {Object[]}
   */
  #sortVehicles(vehicles) {
    const sorted = [...vehicles];
    switch (this.#currentSort) {
    case 'price_asc':
      sorted.sort((a, b) => (a.price?.price ?? 0) - (b.price?.price ?? 0));
      break;
    case 'price_desc':
      sorted.sort((a, b) => (b.price?.price ?? 0) - (a.price?.price ?? 0));
      break;
    case 'year_model_desc':
      sorted.sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
      break;
    case 'year_model_asc':
      sorted.sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
      break;
    case 'driven_asc':
      sorted.sort((a, b) => (a.mileage ?? 0) - (b.mileage ?? 0));
      break;
    case 'driven_desc':
      sorted.sort((a, b) => (b.mileage ?? 0) - (a.mileage ?? 0));
      break;
    default:
      break;
    }
    return sorted;
  }

  /**
   * Reorders the rendered vehicle cards in the grid by matching data-license to
   * vehicle data and appending cards in sorted order. No re-rendering occurs.
   */
  #rerenderVehicleResults() {
    if (!this.#dom.vehicleResultsGrid || !this.#currentSort) return;

    const filtered = this.#vehicleData.filter(
      (v) => v.brand_uuid === this.#currentBrandUuid && v.model_uuid === this.#currentModelUuid,
    );
    if (!filtered.length) return;

    const cardMap = new Map();
    this.#dom.vehicleResultsGrid.querySelectorAll('[data-license]').forEach((card) => {
      cardMap.set(card.dataset.license, card);
    });

    this.#sortVehicles(filtered).forEach((vehicle) => {
      const card = cardMap.get(vehicle.license);
      if (card) this.#dom.vehicleResultsGrid.appendChild(card);
    });
  }

  /**
   * Collapses the results area with a reverse animation, then clears the DOM.
   * Content fades out first, then the grid row collapses, then innerHTML is cleared.
   */
  #hideResults() {
    clearTimeout(this.#dom.resultsHideTimer);
    this.#dom.resultsHideTimer = null;
    this.#clearVehicleResults();

    if (!this.#dom.resultsArea || !this.#dom.resultsContent) return;

    if (!this.#dom.resultsArea.classList.contains('is-visible')) {
      if (this.#dom.results) this.#dom.results.innerHTML = '';
      return;
    }

    this.#dom.resultsContent.classList.remove('is-visible');

    this.#dom.resultsHideTimer = setTimeout(() => {
      if (this.#dom.resultsArea) this.#dom.resultsArea.classList.remove('is-visible');
      this.#dom.resultsHideTimer = setTimeout(() => {
        if (this.#dom.results) this.#dom.results.innerHTML = '';
        this.#dom.resultsHideTimer = null;
      }, TIMINGS.resultsGridCollapse);
    }, TIMINGS.resultsContentFade);
  }

  /**
   * Handles a click on a suggestion chip. Tag chips refine the current query;
   * other chips populate the input and submit the form.
   * @param {HTMLElement} chip - The clicked chip button element
   */
  #handleChipClick(chip) {
    const isTagChip = chip.closest('.ai-car-search__followup-item') !== null;

    if (isTagChip) {
      const filter = chip.dataset.value;
      const label = chip.dataset.label ?? chip.dataset.value;
      if (!filter) {
        console.warn('[AiCarSearch] Tag chip is missing data-value attribute.', chip);
        return;
      }
      const tag = { label, filter };
      const alreadySelected = this.#selectedTags.some((t) => t.filter === filter);
      if (!alreadySelected) {
        this.#selectedTags = [...this.#selectedTags, tag];
      }
      this.#renderSelectedTags();
      this.#lastSearchWasSuggestion = true;
      this.#fetchResults(this.#currentQuery, this.#selectedTags);
    } else {
      const value = chip.dataset.value;
      if (!value) {
        console.warn('[AiCarSearch] Chip is missing data-value attribute.', chip);
        return;
      }
      this.#dom.input.value = value;
      this.#resizeInput();
      this.#dom.input.focus();

      this.#lastSearchWasSuggestion = true;
      if (this.#form.requestSubmit) {
        this.#form.requestSubmit();
      } else {
        this.#form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  }

  /**
   * Handles a click on a .js-search-suggestion element. Inserts the suggestion
   * value into the input and triggers the search.
   * @param {HTMLElement} suggestion - The clicked suggestion element
   */
  #handleSuggestionClick(suggestion) {
    const value = suggestion.dataset.value ?? suggestion.textContent.trim();
    if (!value) return;
    this.#dom.input.value = value;
    this.#resizeInput();
    this.#dom.input.focus();
    this.#lastSearchWasSuggestion = true;
    if (this.#form.requestSubmit) {
      this.#form.requestSubmit();
    } else {
      this.#form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /**
   * Renders the current selected tags into the .js-selected-tags list and
   * toggles the container's visibility via the hidden attribute.
   */
  #renderSelectedTags() {
    if (!this.#dom.selectedTagsList) return;

    // Remove existing chips without touching the reset button sibling
    this.#dom.selectedTagsList.querySelectorAll('.ai-car-search__selected-tag').forEach((el) => el.remove());

    if (this.#selectedTags.length === 0) return;

    const temp = document.createElement('div');
    temp.innerHTML = this.#selectedTags
      .map((tag) => SELECTED_TAG_HTML(tag.label, tag.filter))
      .join('');
    this.#dom.selectedTagsList.prepend(...temp.children);
  }

  /**
   * Removes a selected tag by its filter value and re-fetches results.
   * @param {string} filter - The filter value of the tag to remove
   */
  async #handleSelectedTagRemove(filter) {
    this.#selectedTags = this.#selectedTags.filter((t) => t.filter !== filter);
    this.#renderSelectedTags();
    await this.#refreshStageToken();
    this.#fetchResults(this.#currentQuery, this.#selectedTags);
  }

  /**
   * Steps through the loading text spans at timed intervals.
   * Span 1 is visible by default; span 2 appears after 3 s, span 3 after 6 s.
   */
  #cycleLoadingText() {
    this.#clearLoadingTextTimers();

    const spanEls = Array.from(
      this.#dom.panel?.querySelectorAll('.ai-car-search__loading-text span') ?? [],
    );
    if (spanEls.length === 0) return;

    const hide = (i) => spanEls[i]?.classList.remove('is-visible');
    const show = (i) => spanEls[i]?.classList.add('is-visible');

    show(0);

    const transitionCount = spanEls.length - 1;
    if (transitionCount === 0) return;

    const { loadingTextFirstChange: first, loadingTextLastChange: last, loadingTextTransitionOut: transOut } = TIMINGS;

    for (let i = 0; i < transitionCount; i++) {
      // Evenly distribute change points between first and last.
      // With one transition it fires at `first`; with more they spread to `last`.
      let delay;
      if (transitionCount === 1) {
        delay = first;
      } else {
        delay = first + (i / (transitionCount - 1)) * (last - first);
      }

      this.#loadingTextTimers.push(
        setTimeout(() => {
          hide(i);
          this.#loadingTextTimers.push(setTimeout(() => show(i + 1), transOut));
        }, delay),
      );
    }
  }

  /**
   * Cancels any pending loading text timers.
   */
  #clearLoadingTextTimers() {
    this.#loadingTextTimers.forEach(clearTimeout);
    this.#loadingTextTimers = [];
  }

  /**
   * Resizes the textarea to fit its content on desktop.
   * On mobile (field is column layout) the CSS handles sizing; skip JS resize there.
   */
  #resizeInput() {
    if (getComputedStyle(this.#dom.field).flexDirection === 'column') return;

    this.#dom.input.style.height = 'auto';
    this.#dom.input.style.height = `${this.#dom.input.scrollHeight}px`;
  }

  /**
   * Swaps the submit button between its loading and idle states.
   * @param {boolean} loading - Whether to show the loading state
   */
  #setLoadingState(loading) {
    const submitBtn = this.#dom.field.querySelector('button[type="submit"]');
    if (!submitBtn) return;
    const label = submitBtn.getAttribute('aria-label') ?? 'Hae';
    const replacement = document.createElement('div');

    replacement.innerHTML = loading ? LOADING_BUTTON_HTML(label) : IDLE_BUTTON_HTML(label);
    submitBtn.replaceWith(replacement.firstElementChild);
  }

  /**
   * Renders new HTML into the panel, creating the panel box on first call and
   * animating content transitions on subsequent calls.
   * @param {string} html - Rendered HTML to display
   * @param {Function|null} onComplete - Called after all animations settle
   * @param {Function|null} onInserted - Called immediately after innerHTML is set
   */
  #renderPanel(html, onComplete = null, onInserted = null) {
    if (!this.#dom.panel) {
      return;
    }

    this.#scrollPanelIntoView();

    let box = this.#dom.panel.querySelector('.ai-car-search__panel');

    if (!box) {
      box = document.createElement('div');
      box.className = 'ai-car-search__panel is-entering';
      this.#dom.panel.appendChild(box);
      this.#dom.panel.classList.add('is-visible');
      box.innerHTML = html;
      onInserted?.();
      requestAnimationFrame(() => requestAnimationFrame(() => box.classList.remove('is-entering')));
      this.#animateContentIn(box, onComplete);
      return;
    }

    const existing = box.querySelector('.ai-car-search__panel-content');

    if (existing) {
      const fromHeight = box.offsetHeight;
      existing.classList.add('is-exiting');
      setTimeout(() => this.#transitionContent(box, html, fromHeight, onComplete, onInserted), TIMINGS.panelContentExit);
    } else {
      box.innerHTML = html;
      onInserted?.();
      this.#animateContentIn(box, onComplete);
    }
  }

  /**
   * Animates the panel box from its current height to the height of the new content,
   * then reveals the incoming content. Shrinking transitions complete before revealing;
   * expanding transitions reveal content simultaneously.
   * @param {HTMLElement} box - The persistent panel box element
   * @param {string} html - New HTML to render into the box
   * @param {number} fromHeight - Measured height before the content swap
   * @param {Function|null} onComplete - Called after all animations settle
   * @param {Function|null} onInserted - Called immediately after innerHTML is set
   */
  #transitionContent(box, html, fromHeight, onComplete, onInserted = null) {
    box.style.overflow = 'hidden';
    box.innerHTML = html;
    onInserted?.();

    // Measure natural height before locking it, to avoid scrollHeight >= clientHeight trap
    const toHeight = box.offsetHeight;

    // Pre-hide incoming content so it doesn't flash before its own animation
    box.querySelector('.ai-car-search__panel-content')?.classList.add('is-hidden');

    // Lock to fromHeight and force a reflow so the browser uses it as the transition start point
    box.style.height = `${fromHeight}px`;
    void box.offsetHeight;

    const shrinking = toHeight < fromHeight;

    box.style.transition = `height ${TIMINGS.panelHeightTransition}ms ease`;
    requestAnimationFrame(() => requestAnimationFrame(() => { box.style.height = `${toHeight}px`; }));

    const clearHeight = () => {
      box.style.height = '';
      box.style.overflow = '';
      box.style.transition = '';
    };

    if (shrinking) {
      // Shrink first, then reveal content
      setTimeout(() => { clearHeight(); this.#animateContentIn(box, onComplete); }, TIMINGS.panelShrinkSettle);
    } else {
      // Expand and reveal content simultaneously
      setTimeout(clearHeight, TIMINGS.panelExpandClear);
      this.#animateContentIn(box, onComplete);
    }
  }

  /**
   * Fades in the panel content element, runs the typewriter effect on the response
   * text if present, then cascades in follow-up items before calling onComplete.
   * @param {HTMLElement} box - The persistent panel box element
   * @param {Function|null} onComplete - Called after all animations settle
   */
  #animateContentIn(box, onComplete = null) {
    const el = box.querySelector('.ai-car-search__panel-content');
    if (!el) return;

    // Pre-hide followup items so they don't flash during the content scale-in
    const followupItems = el.querySelectorAll('.ai-car-search__followup-item');
    followupItems.forEach((item) => item.classList.add('is-hidden'));

    el.classList.add('is-hidden');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.remove('is-hidden');
      });
    });

    const responseText = el.querySelector('.ai-car-search__response-text');
    if (responseText) {
      // Blank the text immediately so it's hidden during the scale-in
      const fullText = responseText.textContent;
      responseText.textContent = '';

      // Start typewriter after content has scaled in, then animate followup items,
      // then re-enable the form. .catch ensures onComplete fires even if the chain errors.
      setTimeout(() => {
        this.#typewriterEffect(responseText, fullText)
          .then(() => this.#animateFollowupItems(followupItems))
          .then(() => onComplete?.())
          .catch(() => onComplete?.());
      }, TIMINGS.contentScaleSettle);
    } else {
      // No typewriter — call onComplete after the scale-in transition settles
      setTimeout(() => onComplete?.(), TIMINGS.contentScaleSettle);
    }
  }

  /**
   * Renders text into an element one character at a time at a speed proportional
   * to the text length, clamped between 5 ms and 15 ms per character.
   * @param {HTMLElement} element - Target element to type into
   * @param {string} text - Full text to render character by character
   * @returns {Promise<void>}
   */
  async #typewriterEffect(element, text) {
    // Skip animation for users who prefer reduced motion — set text immediately.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.textContent = text;
      element.setAttribute('aria-live', 'polite');
      this.#announce(text);
      return;
    }

    // Silence screen reader announcements during character-by-character typing.
    // Restore aria-live="polite" once complete so the full text is announced.
    element.setAttribute('aria-live', 'off');
    element.textContent = '';
    const charDelay = Math.max(5, Math.min(15, 1000 / text.length));

    let displayedText = '';
    for (let i = 0; i < text.length; i++) {
      displayedText += text[i];
      element.textContent = displayedText;
      await new Promise((resolve) => setTimeout(resolve, charDelay));
    }

    element.setAttribute('aria-live', 'polite');
    this.#announce(text);
  }

  /**
   * Reveals follow-up question items with a staggered CSS transition,
   * resolving after the last item's transition completes.
   * @param {NodeList} items - Follow-up question item elements to reveal
   * @returns {Promise<void>}
   */
  #animateFollowupItems(items) {
    return new Promise((resolve) => {
      if (!items.length) {
        resolve();
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          items.forEach((item) => item.classList.remove('is-hidden'));
        });
      });

      // Resolve after the last item's transition completes
      const lastDelay = (items.length - 1) * TIMINGS.followupItemStagger + TIMINGS.followupItemTransition;
      setTimeout(resolve, lastDelay);
    });
  }

  /**
   * Scrolls the panel into view if its bottom edge is below the viewport.
   */
  #scrollPanelIntoView() {
    if (!this.#dom.panel) return;
    const rect = this.#dom.panel.getBoundingClientRect();
    if (rect.bottom > window.innerHeight) {
      const target = rect.bottom + window.scrollY - window.innerHeight + 16;
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
  }

  /**
   * Scrolls the element to the top of the viewport only if it is already above
   * the visible area (i.e. the user has scrolled past it). Never scrolls down.
   * @param {Element|null} el - The element to scroll to
   */
  #scrollIntoViewIfAbove(el) {
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top < 0) {
      window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
    }
  }

  /**
   * Animates the panel content out and then removes the panel from the DOM.
   */
  #clearPanel() {
    if (!this.#dom.panel) return;

    const box = this.#dom.panel.querySelector('.ai-car-search__panel');
    const content = box ? box.querySelector('.ai-car-search__panel-content') : null;

    if (!content) {
      this.#dom.panel.classList.remove('is-visible');
      this.#dom.panel.innerHTML = '';
      return;
    }

    content.classList.add('is-exiting');

    setTimeout(() => {
      this.#dom.panel.classList.remove('is-visible');
      this.#dom.panel.innerHTML = '';
    }, TIMINGS.panelContentExit);
  }

  /**
   * Sends a message to the visually-hidden live region for screen readers.
   * Toggling textContent forces a re-announcement even if the text is the same.
   * @param {string} message - The text to announce
   */
  #announce(message) {
    this.#announcer.textContent = '';
    // Brief delay allows the DOM update to register before setting new content.
    requestAnimationFrame(() => { this.#announcer.textContent = message; });
  }

  /**
   * Initialises an AiCarSearch instance for every matching form on the page.
   * @returns {AiCarSearch[]} All created instances
   */
  static init() {
    return Array.from(document.querySelectorAll('.js-ai-car-search-form')).map((form) => new AiCarSearch(form));
  }
}

export default AiCarSearch;
